import { AutocompleteSearchType, CreateTopicRequest, FeatureType, SettingStructure } from '../../../types';
import { LitElement, css, html } from 'lit';
import { cancelDefault, isDefined, undefinedIfEmpty } from '../../../helpers/utils';
import { customElement, property, state } from 'lit/decorators';

import { ColorPicker } from '../../color-picker';
import { ProxiTextfield } from '../../theme/proxi-textfield';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import { dispatchSettingsUpdate } from '../../../helpers/settings';
import { ifDefined } from 'lit/directives/if-defined';
import { logWithAmplitude } from '../../../helpers/api/api';
import { moveItem } from '../../../helpers/tags-categories';
import topicData from '../../../helpers/state/topic-data';

@customElement('setting-component')
export class ToggleandName extends LitElement {
    amplitude_callsite: string = 'Advanced Settings Element';

    @property({ type: Number })
    char_limit?: number;

    @property()
    checked_field?: keyof CreateTopicRequest;

    @property()
    feature_type?: FeatureType;

    @property()
    title: string = '';

    @property()
    description?: string;

    @property()
    upgradeImg?: string;

    @property({type: Boolean})
    is_topic_change: boolean = false;

    @state()
    initial_value_toggle?: boolean;

    @state()
    field_name: string = '';

    @state()
    field_type: string = 'boolean';

    @state()
    input_type: 'inputShort' | 'inputLong' | 'color' | 'dropdown' | 'slider' | 'toggle' | 'orderOption' | 'order' | 'multiSelect'| 'image' = 'inputShort';

    @state()
    is_hidden: boolean = false;

    @state()
    is_subfield: boolean = false;

    @state()
    hasEntitlement: boolean = false;

    @state()
    initial_value_string?: string;

    @state()
    initial_value_vector?: any[];

    @state()
    new_vector_value: any[] = [];

    @state ()
    vector_options?: Array<{text: string; value: any}>;

    @state()
    initial_value_number?: number;

    @state()
    initial_value_boolean?: boolean;

    @state()
    num_max?: number;

    @state()
    num_min?: number;

    @state()
    slider_step?: number;

    @state()
    flipped_langauge?: boolean;

    @state()
    dropdown_options?: Array<{
        value: string;
        label: string;
    }>;

    @state()
    setting_details: SettingStructure = {fieldType: 'boolean', fieldName: this.field_name, title: this.title, flippedLanguage: false, inputType: 'toggle'};

    @state()
    has_been_changed: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    firstUpdated() {
        this.is_hidden = this.setting_details.isHidden ?? false;
        if (this.is_hidden) {
            return;
        }

        // check if user has access to this setting
        if (this.setting_details.hasEntitlement === true ) {
            this.hasEntitlement = true;
        } else if (!isDefined(this.setting_details.hasEntitlement)) {
            this.hasEntitlement = true;
        } else {
            this.getEntitlementIcon();
        }

        // Set the setting type and base settings
        this.field_type = this.setting_details.fieldType;
        this.input_type = this.setting_details.inputType ?? 'inputShort';
        this.title = this.setting_details.title;
        this.field_name = this.setting_details.fieldName;
        this.feature_type = this.setting_details.featureType;
        this.is_topic_change = this.setting_details.isTopic ?? false;
        this.is_subfield = this.setting_details.isSubfield ?? false;
        this.description = this.setting_details.description;

        // Set specific settings based on the field type
        if (this.setting_details.fieldType === 'boolean') {
            this.flipped_langauge = this.setting_details.flippedLanguage ?? false;
            if (this.flipped_langauge) {
                this.initial_value_boolean = !(this.setting_details.initialValueBoolean ?? false);
            } else {
                this.initial_value_boolean = this.setting_details.initialValueBoolean ?? false;
            }
        } else if (this.setting_details.fieldType === 'string') {
            if (isDefined(this.setting_details.textCharLimit)) {
                this.char_limit = this.setting_details.textCharLimit;
            }
            this.initial_value_string = this.setting_details.initialValueString ?? '';
            if (isDefined(this.setting_details.dropdownOptions)) {
                this.dropdown_options = this.setting_details.dropdownOptions;
            }

        } else if (this.setting_details.fieldType === 'number') {
            this.initial_value_number = this.setting_details.initialValueNumber ?? 1;

            if (isDefined(this.setting_details.numberMax)) {
                this.num_max = this.setting_details.numberMax;
            }
            if (isDefined(this.setting_details.numberMin)) {
                this.num_min = this.setting_details.numberMin;
            }
            if (isDefined(this.setting_details.sliderStep)) {
                this.slider_step = this.setting_details.sliderStep;
            }
        } else if (this.setting_details.fieldType === 'vector') {
            this.initial_value_vector = this.setting_details.initialValueVector ?? [];
            this.new_vector_value = this.initial_value_vector ?? [];
            this.vector_options = this.setting_details.vectorOptions ?? [];
        }

        // If input is toggle and toggle is true, dispatch an event to open each subfield
        if (
            this.setting_details.fieldType === 'boolean' &&
            this.setting_details.subfields &&
            this.initial_value_boolean === true
        ) {
            let ishidden = false;
            if (this.field_name === 'disable_command_center_buttons') {
                ishidden = true;
            }
            this.setting_details.subfields.forEach((subfield) => {
                this.dispatchEvent(
                    new CustomEvent('on-toggle-subfield', {
                        detail: {
                            fieldTitle: subfield,
                            hidden: ishidden
                        }
                    })
                );
            });
        }

    }

    // if ishidden changes to true, rerun firstUpdated
    updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'is_hidden') {
                this.firstUpdated();
            }
        });
    }

    private async onChange(e: Event) {

        // prevent propagation
        e.stopPropagation();
        // check that new value is different from old value if string if there has been no change. this is to prevent unnecessary API calls for changes as it loads
        if (!this.has_been_changed) {
            if (this.field_type === 'string') {
                if (this.initial_value_string === (e.target as ProxiTextfield).value) {
                    return;
                }
            } else if (this.field_type === 'number') {
                if (this.initial_value_number === Number((e.target as HTMLInputElement).value)) {
                    return;
                }
            }
        }

        // if this is a toggle, dispatch an event to open each subfield
        if (this.input_type === 'toggle' && this.setting_details.subfields) {
            const ishidden = !(e.target as HTMLInputElement).checked;
            this.setting_details.subfields.forEach((subfield) => {
                this.dispatchEvent(new CustomEvent('on-toggle-subfield', {
                    detail: {
                        fieldTitle: subfield,
                        hidden: ishidden
                    }
                }));
            });
        }

        // if this is the vector search_types, and the vecor contains "Autocomplete", then send message to toggle subfield
        if (this.field_name === 'search_types' && this.setting_details.subfields) {
            const ishidden = !(this.new_vector_value.includes(AutocompleteSearchType.GoogleSearch));
            this.setting_details.subfields.forEach((subfield) => {
                this.dispatchEvent(new CustomEvent('on-toggle-subfield', {
                    detail: {
                        fieldTitle: subfield,
                        hidden: ishidden
                    }
                }));
            });
        }

        // get updatedValue based on input type
        let updatedValue: boolean | string | number | any[] = false;
        if (this.input_type === 'inputShort' || this.input_type === 'inputLong') {
            updatedValue = (e.target as ProxiTextfield).value;
        } else if (this.input_type === 'slider') {
            updatedValue = Number((e.target as HTMLInputElement).value);
        } else if (this.input_type === 'color') {
            updatedValue = (e.target as ColorPicker).value;
        } else if (this.input_type === 'dropdown') {
            updatedValue = (e.target as HTMLInputElement).value;
        } else if (this.input_type === 'toggle') {
            if (this.flipped_langauge === true) {
                updatedValue = !(e.target as HTMLInputElement).checked;
            } else {
                updatedValue = (e.target as HTMLInputElement).checked;
            }
        } else if (this.field_type === 'vector') {
            updatedValue = this.new_vector_value;
        }

        // if the type is a number, make sure to convert it to a number
        if (this.field_type === 'number') {
            updatedValue = Number(updatedValue);
        }

        // create different cases for if it is a topic change or not
        if (this.is_topic_change) {
            dispatchSettingsUpdate(
                {
                    updatedValue,
                    field: this.field_name as keyof CreateTopicRequest,

                },
                this
            );
            logWithAmplitude(this.amplitude_callsite, this.field_name, { value: updatedValue });
        } else {
            // send a custom event with the name of the field and the value
            this.dispatchEvent(new CustomEvent('on-change-value', {
                detail: {
                    field: this.field_name,
                    updatedValue
                }
            }));
            logWithAmplitude(this.amplitude_callsite, this.field_name, { value: updatedValue });
        }

        // mark that the setting has been changed once
        this.has_been_changed = true;
    }

    private async openUpgradeDrawer() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: this.setting_details.featureType ?? FeatureType.HideAllCommandCenterButtons,
                },
            })
        );
    }

    private getEntitlementIcon() {
        this.upgradeImg = '/images/tiers/starter_small.png';
    }

    private onUploadImage(e: CustomEvent) {
        if (e.detail?.imageUrl === undefined) {
            return;
        }
        this.initial_value_string = e.detail.imageUrl;
        this.dispatchEvent(
            new CustomEvent('on-change-value', {
                detail: {
                    field: this.field_name,
                    updatedValue: e.detail.imageUrl,
                },
            })
        );
    }

    private onRemoveImage() {
        this.initial_value_string = '';
        this.dispatchEvent(
            new CustomEvent('on-change-value', {
                detail: {
                    field: this.field_name,
                    updatedValue: '',
                },
            })
        );
    }

    // i want to create a few renders based on the input type and field type
    private renderInput() {
        if (this.input_type === 'inputShort' && this.field_type === 'string') {
            return html`
                <proxi-textfield
                    id="textElementShort"
                    style="width:100%; margin-top: 0.5rem; color: grey; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                    outlined
                    value="${this.initial_value_string ?? ''}"
                    @change=${this.onChange}
                    ?charCounter=${isDefined(this.char_limit)}
                    ?disabled=${!this.hasEntitlement}
                ></proxi-textfield>
            `;
        } else if (this.input_type === 'inputShort' && this.field_type === 'number') {
            return html`
                <proxi-textfield
                    id="numberElementShort"
                    style="width:100%; margin-top: 0.5rem; color: grey; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                    outlined
                    value="${this.initial_value_number ?? 1}"
                    @change=${this.onChange}
                    type="number"
                    min="${ifDefined(this.num_min)}"
                    max="${ifDefined(this.num_max)}"
                    ?disabled=${!this.hasEntitlement}
                ></proxi-textfield>
            `;
        } else if (this.input_type === 'inputLong' && this.field_type === 'string') {
            return html`
                <proxi-textarea
                    id="textElementLong"
                    style="width:100%; margin-top: 0.5rem; color: grey; --mdc-text-field-outlined-idle-border-color: lightgray; --mdc-text-field-ink-color: var(--main-primary-color);"
                    outlined
                    value="${this.initial_value_string ?? ''}"
                    @change=${this.onChange}
                    maxlength="${ifDefined(this.char_limit)}"
                    ?charCounter=${isDefined(this.char_limit)}
                    ?disabled=${!this.hasEntitlement}
                ></proxi-textarea>
            `;
        } else if (this.input_type === 'slider' && this.field_type === 'number') {
            return html`
                <range-slider-component
                    id="sliderElement"
                    style="width:100%; margin-top: 0.5rem; color: grey; height: 60px;"
                    min=${this.num_min ?? 0}
                    max=${this.num_max ?? 100}
                    step=${this.slider_step ?? 1}
                    unit=${''}
                    value=${this.initial_value_number ?? 1}
                    @change=${this.onChange}
                    ?disabled=${!this.hasEntitlement}
                ></range-slider-component>
            `;
        } else if (this.input_type === 'color' && this.field_type === 'string') {
            return html`
                <color-picker
                    id="colorPicker"
                    class="color-picker"
                    size="small"
                    @change=${this.onChange}
                    .open=${false}
                    value=${this.initial_value_string ?? ''}
                    ?disabled=${!this.hasEntitlement}
                ></color-picker>
            `;
        } else if (this.input_type === 'dropdown' && this.field_type === 'string') {
            return html`
                <proxi-selectfield-inline
                    id="dropdownElement"
                    outlined
                    value="${this.initial_value_string ?? this.dropdown_options?.[0].value ?? ''}"
                    @selected=${this.onChange}
                    ?disabled=${!this.hasEntitlement}
                >
                ${this.dropdown_options?.map((option) => html`
                    <mwc-list-item value="${option.value}" style="${this.field_name === 'custom_font' ? `font-family:"${option.value}";--mdc-typography-subtitle1-font-family:"${option.value}"` : ''}">${option.label}</mwc-list-item>
                `)}
                </proxi-selectfield-inline>
            `;
        } else if (this.field_type === 'boolean' &&  this.input_type === 'toggle') {
            return html`
                <div class="setting-switch" @click=${this.hasEntitlement ? '' : this.openUpgradeDrawer}>
                    <form-switch ?mini=${true} class="switch-icon" id="isChecked" name="isChecked" @change=${this.onChange} ?colored=${true} ?checked=${this.initial_value_boolean ?? false} ?disabled=${!this.hasEntitlement}></form-switch>
                </div>
            `;
        } else if (this.field_type === 'vector' && (this.input_type === 'orderOption' || this.input_type === 'order')) {
            return html`
                <div class="vector_order_wrapper">
                    <span class="helpHeader" style="margin-bottom:2px;margin-left:1rem"><i>Drag to reorder. Click (-) to remove</i></span>
                    <div class="vector_order">
                    ${this.new_vector_value?.map(
                        (c, index) => html`
                            ${index !== 0 ? html`<div> <i>then</i> </div>` : ''}
                            <div style="display:flex; flex-direction:row;align-items:center;">
                                <div class="option_wrapper" draggable="true"
                                    @dragstart="${(e: DragEvent) => this.handleDragStart(e)}"
                                    @drop="${async (e: DragEvent) => await this.handlePillDrop(e)}"
                                    @dragenter="${(e: DragEvent) => this.handleDragEnter(e)}"
                                    @dragleave="${(e: DragEvent) => this.handleDragLeave(e)}"
                                    @dragend="${(e: DragEvent) => this.handleDragEnd(e)}"
                                    @dragover="${(e: DragEvent) => cancelDefault(e)}"
                                    index="${index}"
                                    >
                                    ${this.vector_options?.find(option => option.value === c)?.text ?? c}
                                    <button style="background-color:var(--main-warning-color)" @click="${async(e: any) => await this.removeOption(c,e)}">-</button>
                                </div>
                            </div>
                        `
                    )}
                    </div>
                </div>
                ${(this.vector_options?.filter(option => !this.new_vector_value.includes(option.value)).length ?? 1) > 0 ? html`
                    <span class="helpHeader" style="margin-bottom:2px;margin-left:1rem"><i>Unused Options (Click to add to above list)</i></span>
                    <div class="options_wrapper" style="margin-left:1rem">
                        ${this.vector_options?.filter(option => !this.new_vector_value.includes(option.value)).map(
                            (option, index) => html`
                                <div class="option_wrapper">
                                    <span>${option.text}</span>
                                    <button @click="${async (e: any) => await this.addOption(option.value,e)}">+</button>
                                </div>
                            `
                        )}
                    </div>
                ` : ''}
                `;
        } else if (this.field_type === 'vector' && this.input_type === 'multiSelect') {
            return html`
                    <proxi-multi-selectfield
                        outlined
                        .height=${'28px'}
                        title="Add Options"
                        class="input reward-criteria"
                        .options=${this.vector_options ?? []}
                        .selectedValues=${this.new_vector_value.map((item: any) => ({text: this.vector_options?.find(option => option.value === item)?.text, value: item}))}
                        @selection-changed=${async (e: CustomEvent) => {
                            this.new_vector_value = e.detail.selectedValues.map((item: any) => item.value);
                            await this.onChange(e);
                        }}
                        ?disabled=${!this.hasEntitlement}
                    >
                    </proxi-multi-selectfield>
            `;
        } else if (this.field_type === 'string' && this.input_type === 'image') {
            return html`
                ${isDefined(undefinedIfEmpty(this.initial_value_string)) ? html`

                        <div class="image-preview-wrapper" style="display: flex; flex-direction: column; align-items: center; margin-top: 0.5rem;">
                            <img src="${this.initial_value_string ?? ''}"
                                alt="Image preview"
                                class="image-preview"
                                style="max-width: 100%; max-height: 100px; border-radius: 4px; margin-bottom: 0.5rem;"
                                />
                            <span  @click="${this.onRemoveImage}"
                            class="image-preview-text">Change Image</span>
                        </div>
                    ` :  html`
                    <image-upload-component
                        autoUpload
                        .urlOnly="${true}"
                        .activeTab="${'url'}"
                        .imageUrl="${this.initial_value_string ?? ''}"
                        @retreived-autoupload-url="${this.onUploadImage}"
                    ></image-upload-component>
                `}
            `;
        }
    }



    handleDragStart(e: any) {
        e.target.style.opacity = '0.4';
        e.dataTransfer?.setData('text', e.target?.getAttribute('index'));
        e.dataTransfer.dropEffect = 'move';
    }

    handleDragEnter(e: any) {
        e.target.classList.add('over');
        cancelDefault(e);
    }

    handleDragLeave(e: any) {
        e.target.classList.remove('over');
        cancelDefault(e);
    }

    handleDragEnd(e: any) {
        e.target.style.opacity = '1';
        cancelDefault(e);
    }

    async handlePillDrop(e: any) {
        e.target.classList.remove('over');

        const droppedElementIndex = e.dataTransfer?.getData('text');
        const newElementIndex = e.target.getAttribute('index');
        this.new_vector_value = moveItem(this.new_vector_value, droppedElementIndex, newElementIndex);

        await this.onChange(e);

        cancelDefault(e);
    }

    async addOption(option: string, e: any) {
        if (!this.new_vector_value.includes(option)) {
            this.new_vector_value.push(option);
            await this.onChange(e);

        }
        this.requestUpdate();
    }

    async removeOption(option: string, e: any) {
        const optionIndex = this.new_vector_value.indexOf(option);
        if (optionIndex !== -1) {
            this.new_vector_value.splice(optionIndex, 1);
            await this.onChange(e);
        }
        this.requestUpdate();
    }

    render() {
        return html`
                ${this.is_hidden ? '' : html`
                    <div class="setting-section border-grey ${this.setting_details.isSubfield === true ? 'helpSubHeader__subfield' : ''} ${this.hasEntitlement ? '' : 'disabled'}" @click=${this.hasEntitlement ? '' : this.openUpgradeDrawer}>
                        <div class="checkSection ${this.setting_details.isSubfield === true ? 'no-bottom' : ''}">
                            ${this.hasEntitlement || this.setting_details.isSubfield === true ? '' : html`
                                <img class="upgradeImg" src="${this.upgradeImg ?? ''}" />
                            `}
                            <h4 class="helpSubHeader">${this.title}
                                ${this.description ? html`
                                <form-tooltip
                                    tipwidth="180px"
                                    leftPadding="30%"
                                    message="${this.description ?? ''}"
                                >
                                    <mwc-icon>info_outline</mwc-icon>
                                </form-tooltip>` : ''}
                            </h4>
                            ${this.input_type === 'toggle' || this.input_type === 'color' || this.input_type === 'dropdown' ? html`
                                ${this.renderInput()}
                            ` : html``}
                        </div>
                        ${this.input_type !== 'toggle' && this.input_type !== 'color' && this.input_type !== 'dropdown' ? html`
                            ${this.renderInput()}
                        ` : html``}
                    </div>
                `}
		`;
    }

    static get styles() {
        return css`

            .disabled {
                background-color: var(--theme-border-color);
                color: var(--theme-lighter-text-color);
            }

            .upgradeImg {
                height: 0.7rem;
                margin-right: 0.25rem;
            }

            .setting-switch--disabled {
                opacity: 0.5;
                touch-action: none;
                background-color: var(--main-moon-color);
                color: var(--theme-input-color);
            }

            #dropdownElement {
                --mdc-menu-max-height: 200px;
            }

            .setting-switch {
                display: grid;
                grid-auto-flow: column;
                grid-auto-columns: 1fr;
                align-items: center;
                justify-content: space-evenly;
            }

            .description {
                color: var(--theme-input-color);
            }

            .setting-switch--disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            .vector_order_wrapper {
                display: flex;
                flex-direction: column;
                font-size: 0.8rem;
            }

            .pill {
                background-color: #f2f2f2;
                border-radius: 25px;
                padding: 5px 10px;
                margin: 5px;
                cursor: move;
            }

            .helpHeader {
                font-size: 0.7rem;
            }

            .options_wrapper {
                display: inline-flex;
                flex-wrap: wrap;
                font-size: 0.8rem;
                margin-bottom: 10px;
            }

            .vector_order {
                display: flex;
                flex-wrap: wrap;
                margin-bottom: 10px;
                border-radius: 4px;
                align-items: center;
                margin-left: 1rem;
            }

            .option_wrapper {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                background-color: #f2f2f2;
                padding: 5px;
                margin: 2px;
                border-radius: 5px;
            }

            .option_wrapper span {
                flex-grow: 1;
            }

            .option_wrapper button {
                margin-left: 5px;
                background-color: var(--main-success-color);
                border: none;
                color: white;
                text-align: center;
                display: inline-block;
                transition-duration: 0.4s;
                cursor: pointer;
                border-radius: 25px;
            }

            .option_wrapper button:hover {
                background-color: #45a049;
            }

            .switch-label {
                text-align: center;
            }

            .switch-icon {
                margin-left: auto;
                margin-right: auto;
            }

            .gatedField {
                display: flex;
                margin-top: 1rem;
            }

            .gatedSection {
                cursor: pointer;
            }

            .helpSubHeader {
                margin: 0px;
                font-weight: normal;
                font-size: 0.75rem;
                flex: 1;
                color: var(--theme-primary-color);
                display: inline-flex;
                align-items: center;
            }

            .border-grey {
                border-bottom: 1px solid var(--theme-border-color);
            }

            .helpSubHeader__subfield {
                font-size: 0.65rem;
                padding-left: 1rem;
                text-decoration: none;
                width: calc(100% - 1rem);
                border-bottom: none;
            }

            .helpSubHeader__subfield h4 {
                font-size: 0.75rem;
                text-decoration: none;
                border-bottom: none;
            }

            form-tooltip {
                --mdc-icon-size: 14px;
                text-decoration: unset;
                margin-left: 0.25rem;
                color: var(--main-moon-color);
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 20px;
                margin-top: auto;
            }

            .checkSection {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                margin-top: 0.5rem;
            }

            .no-bottom {
                margin-top: 3px;
            }

            .color-picker {
                flex: 1;
            }

            @media only screen and (max-width: 550px) {
                .menuPng {
                    margin-top: 20px;
                }
            }
        `;
    }
}
